import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { assertPrivateSession, getPrivateSessionState } from "@/lib/api/private-session";
import type { SellerAuthSessionResponse } from "@/lib/api/types";
import { signOutSellerFirebaseAuth } from "@/lib/firebase/auth-session";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

const credentials = new WeakMap<QueryClient, { generation: number; scope: string; issuedAt: number }>();
const REFRESH_MS = 50 * 60_000;
const refreshAfterInspection = new WeakSet<QueryClient>();
export const takeSessionCredentialRefresh = (client: QueryClient): boolean => refreshAfterInspection.delete(client);

function authorizationScope({ seller, mustChangePassword }: SellerAuthSessionResponse): string {
  return JSON.stringify([seller.id, seller.storeId, seller.storeSlug, seller.storeKind,
    seller.storeAccess, mustChangePassword, [...(seller.permissions ?? [])].sort()]);
}

/** Session inspection stays fresh; only the optional Firebase credential is reused. */
export async function readSession(client: QueryClient, queryKey: QueryKey, signal: AbortSignal): Promise<SellerAuthSessionResponse> {
  const generation = getPrivateSessionState().generation;
  const previous = client.getQueryData<SellerAuthSessionResponse>(queryKey);
  const credential = credentials.get(client);
  const age = credential ? Date.now() - credential.issuedAt : Infinity;
  const reuse = Boolean(previous?.firebaseToken && credential?.generation === generation &&
    credential.scope === authorizationScope(previous) && age >= 0 && age < REFRESH_MS);
  const request = new AbortController();
  const abort = () => request.abort();
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) abort();
  let timedOut = false;
  const deadline = setTimeout(() => { timedOut = true; abort(); }, 10_000);
  const read = (mint: boolean) => apiJsonClient<SellerAuthSessionResponse>(
    `${endpoints.seller.auth.me}${mint ? "?includeFirebaseToken=true" : ""}`,
    { cache: "no-store", signal: request.signal },
  );
  try {
    let payload = await read(!reuse);
    assertPrivateSession(generation);
    if (request.signal.aborted) throw new DOMException("Session read aborted", "AbortError");
    if (previous && authorizationScope(previous) !== authorizationScope(payload)) {
      void signOutSellerFirebaseAuth().catch(() => undefined);
    }
    if (reuse && authorizationScope(payload) === credential!.scope) {
      payload = { ...payload, firebaseToken: previous!.firebaseToken };
    } else {
      credentials.delete(client);
      if (reuse) {
        // Publish lost/changed access before optional credential recovery can fail.
        refreshAfterInspection.add(client);
        payload = { ...payload, firebaseToken: null };
      }
      assertPrivateSession(generation);
      if (request.signal.aborted) throw new DOMException("Session read aborted", "AbortError");
      if (payload.firebaseToken) credentials.set(client, {
        generation, scope: authorizationScope(payload), issuedAt: Date.now(),
      });
    }
    assertPrivateSession(generation);
    if (request.signal.aborted) throw new DOMException("Session read aborted", "AbortError");
    return payload;
  } catch (error) {
    if (timedOut && !signal.aborted) throw new DOMException("Session read timed out", "TimeoutError");
    throw error;
  } finally {
    clearTimeout(deadline);
    signal.removeEventListener("abort", abort);
  }
}
